// Central business + content data (NAP consistency across the site)
import {
  Bone, Move, PersonStanding,
  ShieldCheck, HeartPulse, Stethoscope, HandHeart, UserCheck, Sparkles,
} from "lucide-react";
import { ReactComponent as BackPainIcon } from "@/assets/condition-icons/back-pain.svg";
import { ReactComponent as NeckPainIcon } from "@/assets/condition-icons/neck-pain.svg";
import { ReactComponent as ShoulderPainIcon } from "@/assets/condition-icons/shoulder-pain.svg";
import { ReactComponent as SciaticaIcon } from "@/assets/condition-icons/sciatica.svg";
import { ReactComponent as SpineIcon } from "@/assets/condition-icons/spine-discomfort.svg";
import { ReactComponent as JointStiffnessIcon } from "@/assets/condition-icons/joint-stiffness.svg";
import { ReactComponent as PostureIcon } from "@/assets/condition-icons/posture-problem.svg";
import { ReactComponent as MobilityIcon } from "@/assets/condition-icons/mobility.svg";

export const SITE_URL = "https://www.drsatyampainhealthcare.com";

export const business = {
  doctorName: "Dr. Satyam Jaiswal",
  clinicName: "Dr. Satyam's Pain Healthcare",
  role: "Chiropractor | Pain & Spine Specialist",
  city: "Varanasi",
  tagline: "Aap Jeetenge, Dard Haarega",
  taglineEn: "You will win, the pain will lose",
  phoneDisplay: "+91 81879 71516",
  phoneTel: "+918187971516",
  whatsapp: "918187971516",
  qualifications: "MD (Chiro), MPTH (Ortho), BPTH, FNMT (UK), CMOT (Spine), MIAP, COMT (Bangalore), DMCT (London)",
  address: {
    line1: "Sonkar Market, D63/69-A",
    line2: "Sigra-Mahmoorganj Road, Near Padmini Hotel",
    area: "Sigra",
    city: "Varanasi",
    state: "Uttar Pradesh",
    pin: "221010",
    landmark: "Near Padmini Hotel",
  },
  timings: [
    { days: "Monday – Saturday", hours: "9:00 AM – 8:00 PM" },
    { days: "Sunday", hours: "9:00 AM – 3:00 PM" },
  ],
  stats: [
    { value: "25,000+", label: "Patients Treated" },
    { value: "1,000+", label: "Google Reviews" },
    { value: "10+", label: "Years Experience" },
  ],
  socials: {
    instagram: "https://www.instagram.com/drsatyamchiropractor/",
    youtube: "https://www.youtube.com/@DrSatyamchiropractor",
    facebook: "https://www.facebook.com/DrSatyamChiropractor/",
    linkedin: "https://www.linkedin.com/in/satyamjaiswalphysio",
    google: "https://maps.app.goo.gl/WtPQ2gsJGTQypvNp7",
  },
};

export const fullAddress = `${business.address.line1}, ${business.address.line2}, ${business.address.area}, ${business.address.city}, ${business.address.state} - ${business.address.pin}`;

export const mapEmbedSrc =
  "https://www.google.com/maps?q=" +
  encodeURIComponent("Dr. Satyam's Pain Healthcare, Sigra-Mahmoorganj Road, Sigra, Varanasi, Uttar Pradesh 221010") +
  "&output=embed";

export function whatsappLink(message) {
  const text = encodeURIComponent(message || "Hello Dr. Satyam, I would like to book an appointment.");
  return `https://wa.me/${business.whatsapp}?text=${text}`;
}

export const routes = {
  home: "/",
  about: "/dr-satyam-jaiswal-chiropractor-varanasi",
  treatments: "/chiropractic-treatment-varanasi",
  symptoms: "/chiropractic-symptoms-varanasi",
  testimonials: "/patient-testimonials-varanasi",
  resources: "/resources-varanasi",
  contact: "/contact-chiropractor-varanasi",
  privacy: "/privacy-policy",
  terms: "/terms-and-conditions",
};

export const navLinks = [
  { label: "Home", to: routes.home },
  { label: "About Dr.", to: routes.about },
  { label: "Treatments", to: routes.treatments },
  { label: "Symptoms", to: routes.symptoms },
  { label: "Testimonials", to: routes.testimonials },
  { label: "Resources", to: routes.resources },
  { label: "Contact Us", to: routes.contact },
];

export const images = {
  doctor: "/dr-satyam-jaiswal-chiropractor-varanasi.png",
  doctorHero: "/dr-satyam-jaiswal-portrait.webp",
  adjustment: "https://images.unsplash.com/photo-1519824145371-296894a0daa9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  clinic: "https://images.unsplash.com/photo-1629909614456-6b1c5c94cecc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  consult: "https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  neck: "https://images.unsplash.com/photo-1767972463825-96eaa9e0766b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  back: "https://images.unsplash.com/photo-1706353399656-210cca727a33?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  spine: "https://images.unsplash.com/photo-1714929818437-889f2c93a94a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

export const conditions = [
  { icon: BackPainIcon, name: "Back Pain", desc: "Lower back discomfort and movement-related pain." },
  { icon: NeckPainIcon, name: "Neck Pain", desc: "Stiffness, reduced mobility and posture-related neck concerns." },
  { icon: ShoulderPainIcon, name: "Shoulder Pain", desc: "Discomfort affecting reach and daily movement." },
  { icon: SciaticaIcon, name: "Sciatica", desc: "Radiating leg discomfort linked to the lower spine." },
  { icon: SpineIcon, name: "Spine Discomfort", desc: "Assessment and personalized support for spine concerns." },
  { icon: JointStiffnessIcon, name: "Joint Stiffness", desc: "Care for stiff, restricted joints affecting movement." },
  { icon: PostureIcon, name: "Posture Problems", desc: "Guidance for posture-related strain and imbalance." },
  { icon: MobilityIcon, name: "Mobility Concerns", desc: "Support to help you move with greater ease." },
];

export const treatments = [
  { icon: HandHeart, name: "Chiropractic Care", desc: "Hands-on, non-surgical care focused on the spine and joints to support better movement." },
  { icon: HeartPulse, name: "Pain Management", desc: "A personalized approach aimed at understanding and easing everyday pain." },
  { icon: Bone, name: "Spine Care", desc: "Careful assessment and care for spine-related discomfort and mobility." },
  { icon: PersonStanding, name: "Posture Correction", desc: "Guidance and care to help address posture-related strain." },
  { icon: Move, name: "Mobility Improvement", desc: "Support to help restore comfortable, functional movement." },
  { icon: ShieldCheck, name: "Non-Surgical Care", desc: "Appropriate, conservative care options where suitable for your concern." },
];

export const whyChoose = [
  { icon: UserCheck, title: "Personalized Approach", desc: "Care plans built around your specific concern, not a one-size-fits-all routine." },
  { icon: HeartPulse, title: "Patient-Focused Care", desc: "We listen first, explain clearly, and keep you involved at every step." },
  { icon: ShieldCheck, title: "Non-Surgical Options", desc: "Conservative, hands-on care approaches where appropriate for your condition." },
  { icon: Move, title: "Focus on Mobility", desc: "The goal is better movement, comfort and long-term functional improvement." },
  { icon: Stethoscope, title: "Modern Environment", desc: "A clean, comfortable and modern clinical setting in the heart of Varanasi." },
  { icon: Sparkles, title: "Patient Education", desc: "Understand the 'why' behind your pain so you can care for yourself better." },
];

export const process = [
  { step: "01", title: "Consultation", desc: "We begin by listening to your concern and understanding your history." },
  { step: "02", title: "Understanding Your Concern", desc: "A careful assessment to understand the underlying cause, not just symptoms." },
  { step: "03", title: "Personalized Care Plan", desc: "A care approach tailored to your body, lifestyle and goals." },
  { step: "04", title: "Treatment & Progress Monitoring", desc: "Ongoing care with regular review of your progress and comfort." },
];

export const areasOfExpertise = [
  "Chiropractic Care", "Spine Health", "Back Pain Management", "Neck Pain Management",
  "Posture Care", "Mobility Improvement", "Sports-Related Concerns", "Musculoskeletal Care",
];

export const symptoms = [
  { name: "Lower Back Pain", desc: "Persistent or recurring discomfort in the lower back.", impact: "Can make sitting, standing, lifting and sleeping uncomfortable.", when: "If pain lasts more than a few days or keeps returning, a professional assessment may help." },
  { name: "Neck Pain", desc: "Discomfort or stiffness in the neck and upper shoulders.", impact: "May limit head movement and affect focus, work and rest.", when: "When stiffness lingers, worsens or affects daily activities." },
  { name: "Shoulder Pain", desc: "Discomfort affecting the shoulder joint and surrounding muscles.", impact: "Can restrict reaching, lifting and overhead movement.", when: "If reduced movement or discomfort continues over time." },
  { name: "Sciatica", desc: "Discomfort that may radiate from the lower back into the leg.", impact: "May affect walking, sitting and everyday comfort.", when: "Radiating discomfort or tingling is worth a professional consultation." },
  { name: "Tingling or Numbness", desc: "Unusual sensations in the arms, hands, legs or feet.", impact: "Can be distracting and may affect grip or movement.", when: "Persistent tingling or numbness should be assessed professionally." },
  { name: "Joint Stiffness", desc: "Reduced flexibility and a feeling of tightness in the joints.", impact: "Can make everyday movements feel restricted or effortful.", when: "When stiffness limits your normal range of movement." },
  { name: "Poor Posture", desc: "Rounded shoulders, forward head or an uneven stance.", impact: "May contribute to ongoing strain and discomfort over time.", when: "If posture-related strain is affecting your comfort or confidence." },
  { name: "Reduced Mobility", desc: "Difficulty moving freely or performing usual activities.", impact: "Can affect independence, exercise and quality of life.", when: "When reduced movement is limiting your daily routine." },
  { name: "Spine Discomfort", desc: "General discomfort along the spine and back region.", impact: "May affect posture, movement and overall comfort.", when: "Ongoing spine discomfort is worth a careful assessment." },
];

export const faqs = [
  { q: "What does a chiropractor do?", a: "A chiropractor focuses on the health of your spine, joints and muscles using hands-on, non-surgical care approaches. The aim is to support better movement, ease discomfort and help you understand your concern." },
  { q: "Is chiropractic care suitable for back and neck pain?", a: "Chiropractic care is commonly sought for back pain, neck pain, posture concerns and mobility issues. After a careful assessment, Dr. Satyam will advise whether it is appropriate for your specific concern." },
  { q: "What happens during my first visit?", a: "Your first visit begins with a consultation where we listen to your concern and history, followed by an assessment. Based on this, a personalized care plan is discussed with you." },
  { q: "Is chiropractic care non-surgical?", a: "Yes. Chiropractic care focuses on conservative, non-surgical approaches. Where appropriate, Dr. Satyam recommends suitable care options and will refer onward if needed." },
  { q: "How do I book an appointment?", a: "You can book an appointment using the 'Book Appointment' button, call the clinic directly, or message us on WhatsApp. We will help you find a convenient time." },
  { q: "Where is the clinic located?", a: `The clinic is at ${fullAddress}. It is easy to reach and located near Padmini Hotel in Sigra, Varanasi.` },
];

export const videoTestimonials = [
  "DFAg3ysTaD1", "DapzkyNCpJ4", "DVTMsT9AqDj", "DP6OY25gWJ4", "DPgX3kpgb4w",
  "Dcf8PJTDPtS", "DVYM_Qck7kO", "Da7k41Tj7rt", "DYZp0rmEjXh", "DX_DFK1Adqi",
  "DXrB63qAg63", "DWOc1QbDFtl", "DRpFGYFARPL", "DRTy58kAZQ0", "DQHKTCWAS73",
  "DNnb0bph3zq", "DNaa6PzBiKY",
];

export const testimonials = [
  { name: "Rahul Verma", area: "Sigra, Varanasi", rating: 5, text: "I had been struggling with lower back pain for months. Dr. Satyam explained everything clearly and the personalized care made a real difference to my daily comfort." },
  { name: "Anjali Singh", area: "Lanka, Varanasi", rating: 5, text: "Very professional and patient. My neck stiffness improved and I finally understood what was causing it. Highly recommend for anyone with posture problems." },
  { name: "Mohd. Imran", area: "Bhelupur, Varanasi", rating: 5, text: "The clinic is clean and modern, and Dr. Satyam takes time to listen. The non-surgical approach worked well for my shoulder discomfort." },
  { name: "Priya Gupta", area: "Mahmoorganj, Varanasi", rating: 5, text: "Genuine care and no false promises. My mobility has improved and I feel much more confident with my movement now." },
  { name: "Sandeep Yadav", area: "Cantt, Varanasi", rating: 5, text: "Excellent experience. The step-by-step approach helped me understand my sciatica and manage it far better than before." },
  { name: "Neha Sharma", area: "Sigra, Varanasi", rating: 5, text: "Friendly, knowledgeable and reassuring. The team focuses on long-term improvement rather than quick fixes. Thank you, Dr. Satyam." },
];
